import React from 'react';
import {
  Badge,
  Binding2Icon,
  Button,
  ChevronLargeDownIcon,
  ChevronLargeRightIcon,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DeleteIcon,
  DesktopLargeBreakpointIcon,
  DesktopStarBreakpointIcon,
  DesktopXLBreakpointIcon,
  DesktopXXLBreakpointIcon,
  HStack,
  IconButton,
  MobileBreakpointIcon,
  MobileLandscapeBreakpointIcon,
  TabletPortraitBreakpointIcon,
  Tag,
  Text,
  Tooltip,
  View,
  VStack,
  useCollapsible,
  type IconComponent,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {
  humanizeRgb,
  isValidColor,
  getHex,
  isTransparent,
  isPartiallyTransparent,
} from '@packages/systems/core/utils/ColorUtils';
import {
  diffStyleLessProperties,
  splitCssValue,
} from '@packages/systems/core/utils/CssStringUtils';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import type {
  VariableMap,
  VariableCollectionMap,
  VariableID,
  ColorPrimitive,
} from '@packages/systems/core/utils/StyleVariables';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {selectSlugMap} from '@packages/systems/libraries';
import type {Plugins} from '@packages/systems/wfdl';
import {getBreakpointLabel} from '@packages/systems/style/breakpoints-config';
import {getBreakpointID} from '@packages/systems/style/types';
import type {BreakpointID} from '@packages/systems/style/types';
import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';

import type {
  TextDraftContent,
  VCCStyleOverridesMap,
  VCCTextOverridesMap,
} from './flux/actionCreators';
import {getShadowDomElementPresentation} from './shadowDomElementPresentation';
import {
  contentToText,
  summarizeTextOverrideEffects,
  vccTextOverrideHasChanges,
} from './flux/vccTextOverrideUtils';

export const DEFAULT_EMPTY_TEXT = 'All changes have been removed.';

export interface StyleChangesElementGroup {
  key: string;
  nodeId: string;
  tagName: string;
  textContent: string;
  /**
   * The element renders content from a component prop (EXT-1840). Captured
   * when the draft is created; absent means "unknown or not connected".
   */
  hasConnectedProp?: true;
  variant?: {id: string; displayName: string};
  changedProperties: Array<{
    property: string;
    value: string;
    breakpointId?: string;
    pseudoState?: string | null;
    /**
     * A text draft's modified content, for telling one edit on an element from
     * the next: `value` is an effects summary and `textContent` the pre-edit
     * text, so neither moves when the copy changes. Absent on style properties.
     */
    textDraftContent?: TextDraftContent;
  }>;
}

// Folds VCC draft changes into UI groups keyed by (nodeId, variantId). Each
// group lists the style properties and text content whose modified value
// differs from original.
export function buildElementGroups(
  overrides: VCCStyleOverridesMap,
  textOverrides: VCCTextOverridesMap = {}
): StyleChangesElementGroup[] {
  const groups: StyleChangesElementGroup[] = [];
  const groupsByKey = new Map<string, StyleChangesElementGroup>();
  const connectedPropByKey = new Map<string, boolean>();

  const getOrCreateGroup = (entry: {
    nodeId: string;
    tagName: string;
    textContent: string;
    hasConnectedProp?: boolean;
    variant?: {id: string; displayName: string};
  }) => {
    const groupKey = `${entry.nodeId}::${entry.variant?.id ?? ''}`;
    let group = groupsByKey.get(groupKey);
    const entryHasConnectedProp = entry.hasConnectedProp === true;
    if (!group) {
      group = {
        key: groupKey,
        nodeId: entry.nodeId,
        tagName: entry.tagName,
        textContent: entry.textContent,
        ...(entry.hasConnectedProp ? {hasConnectedProp: true} : {}),
        variant: entry.variant,
        changedProperties: [],
      };
      groupsByKey.set(groupKey, group);
      connectedPropByKey.set(groupKey, entryHasConnectedProp);
      groups.push(group);
    } else {
      const allEntriesAreConnected =
        connectedPropByKey.get(groupKey) === true && entryHasConnectedProp;
      connectedPropByKey.set(groupKey, allEntriesAreConnected);
      group.hasConnectedProp = allEntriesAreConnected ? true : undefined;
    }
    return group;
  };

  for (const entry of Object.values(overrides)) {
    const changed = diffStyleLessProperties(
      entry.original || '',
      entry.modified || ''
    );
    if (changed.length === 0) continue;

    const group = getOrCreateGroup(entry);
    for (const prop of changed) {
      group.changedProperties.push({
        property: prop.property,
        value: prop.value,
        breakpointId: entry.breakpointId,
        pseudoState: entry.pseudoState,
      });
    }
  }

  for (const entry of Object.values(textOverrides)) {
    if (!vccTextOverrideHasChanges(entry.original, entry.modified)) continue;

    const group = getOrCreateGroup({
      nodeId: entry.nodeId,
      tagName: entry.tagName,
      textContent: contentToText(entry.original),
      ...(entry.hasConnectedProp ? {hasConnectedProp: true} : {}),
      variant: entry.variant,
    });
    group.changedProperties.push({
      property: 'text',
      value: summarizeTextOverrideEffects(entry.original, entry.modified),
      textDraftContent: entry.modified,
    });
  }

  return groups;
}

const BREAKPOINT_ICONS: Record<BreakpointID, IconComponent> = {
  [BREAKPOINT_ID_XXL]: DesktopXXLBreakpointIcon,
  [BREAKPOINT_ID_XL]: DesktopXLBreakpointIcon,
  [BREAKPOINT_ID_LARGE]: DesktopLargeBreakpointIcon,
  [BREAKPOINT_ID_MAIN]: DesktopStarBreakpointIcon,
  [BREAKPOINT_ID_MEDIUM]: TabletPortraitBreakpointIcon,
  [BREAKPOINT_ID_SMALL]: MobileLandscapeBreakpointIcon,
  [BREAKPOINT_ID_TINY]: MobileBreakpointIcon,
};

const COLOR_PROPERTIES = new Set([
  'color',
  'background-color',
  'background',
  'border-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  'caret-color',
  'column-rule-color',
  'fill',
  'stroke',
  'flood-color',
  'lighting-color',
  'stop-color',
  'accent-color',
]);

const SHADOW_PROPERTIES = new Set(['box-shadow', 'text-shadow']);

const CHECKERBOARD_LAYERS = [
  'linear-gradient(45deg, #d9d9d9 25%, transparent 25%, transparent 75%, #d9d9d9 75%)',
  'linear-gradient(45deg, #d9d9d9 25%, transparent 25%, transparent 75%, #d9d9d9 75%)',
].join(', ');

const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage: CHECKERBOARD_LAYERS,
  backgroundSize: '6px 6px, 6px 6px',
  backgroundPosition: '0 0, 3px 3px',
  backgroundColor: '#fff',
};

function getColorSwatchStyle(color: string): React.CSSProperties {
  if (isTransparent(color)) {
    return CHECKERBOARD_STYLE;
  }
  if (isPartiallyTransparent(color)) {
    return {
      backgroundImage: `linear-gradient(${color}, ${color}), ${CHECKERBOARD_LAYERS}`,
      backgroundSize: '100%, 6px 6px, 6px 6px',
      backgroundPosition: '0 0, 0 0, 3px 3px',
      backgroundColor: '#fff',
    };
  }
  return {backgroundColor: getHex(color)};
}

// Pulls the color portion out of a CSS shadow value so the swatch reflects the
// actual shadow color instead of feeding offsets/blur into tinycolor (which
// silently resolves them to a wrong color). Returns the first shadow's color
// (or its `@var_<id>` placeholder) for multi-shadow values — the section is
// an at-a-glance summary.
export function extractColorFromShadow(value: string): string | null {
  const firstSegment = String(splitCssValue(value, ',')[0] ?? '');

  const fnMatch = firstSegment.match(/(?:rgba?|hsla?)\([^)]*\)/i);
  if (fnMatch) return fnMatch[0];

  const hexMatch = firstSegment.match(/#[0-9a-fA-F]{3,8}\b/);
  if (hexMatch) return hexMatch[0];

  for (const token of firstSegment.trim().split(/\s+/)) {
    if (!token || token === 'inset') continue;
    if (/^-?\d/.test(token)) continue;
    if (StyleVariables.isVariablePlaceholder(token)) return token;
    if (isValidColor(token)) return token;
  }

  return null;
}

// Returns the variable id only when the trimmed value is itself a
// `@var_<id>` placeholder. Without the anchor, embedded placeholders like
// `2px 4px 8px @var_<id>` would mis-route shadows through `VariablePill`.
export function getWholeValueVariableId(
  normalizedValue: string
): VariableID | undefined {
  const trimmed = normalizedValue.trim();
  if (!StyleVariables.isVariablePlaceholder(trimmed)) return undefined;
  return StyleVariables.getVariableIdFromPlaceholder(trimmed);
}

// `normalizedValue` must already have been run through
// `replaceCssVariableNamesWithVarIds` — variable references must appear as
// `@var_<id>` placeholders for resolution to succeed.
function resolveShadowSwatchStyle(
  normalizedValue: string,
  variables: VariableMap
): React.CSSProperties | undefined {
  const colorToken = extractColorFromShadow(normalizedValue);
  if (colorToken == null) return undefined;

  const variableId = StyleVariables.getVariableIdFromPlaceholder(colorToken);
  if (variableId != null) {
    const resolved = StyleVariables.resolvePreviewValue<ColorPrimitive>(
      variables,
      variableId
    );
    if (!(resolved instanceof Error) && resolved.type === 'color') {
      return getColorSwatchStyle(resolved.value);
    }
    return undefined;
  }

  if (!isValidColor(colorToken)) return undefined;
  return getColorSwatchStyle(colorToken);
}

function formatPropertyValue(
  value: string,
  property: string
): {displayValue: string; swatchStyle: React.CSSProperties | undefined} {
  if (COLOR_PROPERTIES.has(property) && isValidColor(value)) {
    return {
      displayValue: humanizeRgb(value),
      swatchStyle: getColorSwatchStyle(value),
    };
  }
  return {displayValue: value, swatchStyle: undefined};
}

const ColorSwatchStyled = emotionStyled('span')({
  display: 'inline-block',
  width: '12px',
  height: '12px',
  borderRadius: '2px',
  border: '1px solid $border1',
  flexShrink: 0,
  verticalAlign: 'middle',
});

const ColorSwatch = ({style}: {style?: React.CSSProperties}) => (
  <ColorSwatchStyled style={style} data-automation-id="style-changes-swatch" />
);

const selectAllVariables = (state: DesignerStores): VariableMap =>
  state.CssVariablesStore.variables;

const selectVariableCollections = (
  state: DesignerStores
): VariableCollectionMap => state.CssVariablesStore.variableCollections;

// Mirrors the color/label fallback used by `StyleVariableReferenceButton` in
// `public/js/designer-flux/components/StyleVariableBindMenu.tsx` so the pill
// behaves consistently with the Style Panel for unknown / deleted variables.
const VariablePill = ({
  variableId,
  variables,
}: {
  variableId: VariableID;
  variables: VariableMap;
}) => {
  const variable = variables.get(variableId);
  const displayName = variable
    ? StyleVariables.getVariableDisplayName(variable)
    : 'Unknown variable';
  const colorVariant =
    variable == null ? 'red' : variable.deleted ? 'yellow' : 'purple';

  let swatchStyle: React.CSSProperties | undefined;
  if (variable?.type === 'color') {
    const resolved = StyleVariables.resolvePreviewValue<ColorPrimitive>(
      variables,
      variableId
    );
    if (!(resolved instanceof Error) && resolved.type === 'color') {
      swatchStyle = getColorSwatchStyle(resolved.value);
    }
  }

  return (
    <Tag
      density="compact"
      colorVariant={colorVariant}
      title={displayName}
      data-automation-id="style-changes-variable-pill"
    >
      <PillContent>
        {swatchStyle && <ColorSwatch style={swatchStyle} />}
        {displayName}
      </PillContent>
    </Tag>
  );
};

const PillContent = emotionStyled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '$1',
});

const PropertyValueRenderer = ({
  property,
  value,
  variables,
  variableCollections,
  slugMap,
}: {
  property: string;
  value: string;
  variables: VariableMap;
  variableCollections: VariableCollectionMap;
  slugMap: Plugins.PluginsType['slugMap'];
}) => {
  // The AI Assistant designer context may ship variable bindings as resolved
  // `var(--<name>)` references so the LLM can preserve them
  // (`serializeVCCStyleOverridesForAssistant` / APAI-3230). Convert back to
  // the internal `@var_<id>` form so the existing pill-detection logic — and
  // any leftover unresolved overrides — share a single code path.
  const normalized = StyleVariables.replaceCssVariableNamesWithVarIds(
    value,
    variables,
    variableCollections,
    slugMap
  );
  const variableId = getWholeValueVariableId(normalized);

  if (variableId != null) {
    return (
      <HStack
        spacing={1}
        align="center"
        minWidth="$0"
        data-automation-id="style-changes-value"
      >
        <VariablePill variableId={variableId} variables={variables} />
      </HStack>
    );
  }

  const {displayValue, swatchStyle} = SHADOW_PROPERTIES.has(property)
    ? {
        displayValue: value,
        swatchStyle: resolveShadowSwatchStyle(normalized, variables),
      }
    : formatPropertyValue(value, property);

  return (
    <HStack spacing={1} align="center" data-automation-id="style-changes-value">
      {swatchStyle && <ColorSwatch style={swatchStyle} />}
      <Text truncateToLines={1} data-automation-id="style-changes-value-text">
        {displayValue}
      </Text>
    </HStack>
  );
};

export type StyleChangesListLayout = 'default' | 'toolbar' | 'assistant';

type ExpandableStyleChangesListLayout = Exclude<
  StyleChangesListLayout,
  'default'
>;

// `:has(:focus-visible)` rather than `:focus-within`, here and on
// `ToolbarGroupHeader`: opening a group focuses its trigger, and under
// `:focus-within` that keeps the remove button visible and the chevron swapped
// in long after the pointer leaves. `:focus-visible` does not match a mouse
// click, so the row returns to rest, while a keyboard user still reveals the
// button and can tab to it — which is what the focus half of the selector is
// for, since a `visibility: hidden` button is not focusable. Same shape Spring
// uses in `RowItem.styles.ts`.
const expandableHeaderRowStyles = {
  marginTop: '$1',
  padding: '$1 $0 $1 $2',
  '&:is(:hover, :has(:focus-visible)) [data-style-changes-remove-container]': {
    visibility: 'visible',
  },
} as const;

const assistantHeaderRowStyles = {
  padding: '$1 $2',
  '&:is(:hover, :has(:focus-visible)) [data-style-changes-remove-container]': {
    visibility: 'visible',
  },
} as const;

const expandableRemoveContainerStyles = {
  marginLeft: '$0',
  visibility: 'hidden',
} as const;

const expandablePropertyRowStyles = {
  marginLeft: '$6',
  justifyContent: 'flex-start',
  '& [data-automation-id="style-changes-value"]': {
    flexShrink: 100,
    minWidth: '$16',
    overflow: 'hidden',
  },
  '&[data-is-last-row]': {
    marginBottom: '$2',
  },
} as const;

const ScrollArea = emotionStyled(VStack)(
  {
    maxHeight: '240px',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '$1 0',
  },
  {
    variants: {
      layout: {
        default: {},
        toolbar: {
          padding: '$0 $0 $2',
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
        },
        assistant: {
          padding: '$1 $0 $0',
        },
      },
    },
  }
);

const ElementHeaderRow = emotionStyled(HStack)(
  {
    width: '100%',
    padding: '$1 $1 $1 $3',
    alignItems: 'center',
    gap: '$2',
    minWidth: 0,
    minHeight: '$6',
  },
  {
    variants: {
      layout: {
        default: {},
        toolbar: expandableHeaderRowStyles,
        assistant: assistantHeaderRowStyles,
      },
    },
  }
);

const ElementLabel = emotionStyled(Text)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const RemoveGroupButtonContainer = emotionStyled(View)(
  {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  {
    variants: {
      layout: {
        default: {},
        toolbar: expandableRemoveContainerStyles,
        assistant: expandableRemoveContainerStyles,
      },
    },
  }
);

// Tighter padding keeps the hit target visually centered without increasing
// the row height.
const RemoveGroupButton = emotionStyled(IconButton)({
  padding: '$1',
});

const ToolbarGroupHeader = emotionStyled(HStack)({
  width: '100%',
  minWidth: 0,
  borderRadius: '$2',
  // See `expandableHeaderRowStyles` for why this is not `:focus-within`.
  '&:is(:hover, :has(:focus-visible)) [data-style-changes-element-icon]': {
    display: 'none',
  },
  '&:is(:hover, :has(:focus-visible)) [data-style-changes-chevron]': {
    display: 'flex',
  },
});

const ToolbarGroupTrigger = emotionStyled(Button)({
  cursor: 'pointer',
  minWidth: 0,
  flexShrink: 1,
  '& > span, & > span > span': {
    minWidth: 0,
  },
  '&&, &&:hover, &&:active, &&[aria-expanded="true"]': {
    backgroundColor: 'transparent',
  },
});

const ToolbarGroupIcon = emotionStyled(View)({
  width: '$4',
  height: '$4',
  flexShrink: 0,
});

const ToolbarGroupChevron = emotionStyled(View)({
  display: 'none',
});

const ToolbarGroupCount = emotionStyled(Text)({
  marginLeft: 'auto',
  paddingRight: '$1',
  whiteSpace: 'nowrap',
});

const PropertyRow = emotionStyled(HStack)(
  {
    justifyContent: 'space-between',
    padding: '$0.5 $3',
    minHeight: '$6',
  },
  {
    variants: {
      layout: {
        default: {},
        toolbar: {
          ...expandablePropertyRowStyles,
          width: '224px',
        },
        assistant: {
          ...expandablePropertyRowStyles,
          // The AIA card is itself about as narrow as the toolbar popover's
          // fixed row width, so fill the remaining space instead — a
          // hard-coded 224px plus the indent overflows the card's clip box.
          width: 'auto',
          alignSelf: 'stretch',
        },
      },
    },
  }
);

const GroupDivider = emotionStyled(View)({
  borderTop: '1px solid $borderDefault',
  margin: '$1 0',
});

const PropertyName = emotionStyled(Text)({
  whiteSpace: 'nowrap',
});

/**
 * The property half of one drafted change: breakpoint icon, pseudo-state
 * badge, property name — the qualifiers first, so `color` at `:hover` on
 * tablet is not mistaken for plain `color`.
 *
 * Exported for the chat-history card (`VisualEditsMessageCard`), which renders
 * the same pairs from a persisted summary and has to agree with this list
 * about what a change looks like. It renders its own grid, so the property and
 * value halves are separate components rather than one row: they sit in
 * different grid cells so the value column can line up card-wide.
 */
export const StyleChangePropertyLabel = ({
  property,
  pseudoState,
  breakpointId,
  breakpointDisplay = 'icon',
}: {
  property: string;
  pseudoState?: string | null;
  breakpointId?: string;
  /**
   * How the breakpoint is shown. `icon` spends a 16px column on the breakpoint
   * glyph. `tooltip` spends nothing: the breakpoint rides a tooltip on the row,
   * for a surface too narrow to give the glyph a column and still leave the
   * property name readable.
   */
  breakpointDisplay?: 'icon' | 'tooltip';
}) => {
  const breakpointID = breakpointId ? getBreakpointID(breakpointId) : null;
  const breakpointLabel = breakpointID
    ? getBreakpointLabel(breakpointID)
    : null;
  const BreakpointIcon = breakpointID ? BREAKPOINT_ICONS[breakpointID] : null;
  const asTooltip = breakpointDisplay === 'tooltip' && breakpointLabel != null;

  const row = (
    <HStack
      spacing={1}
      align="center"
      minWidth="$0"
      flexShrink={1}
      data-automation-id="style-changes-property"
    >
      {breakpointDisplay === 'icon' && BreakpointIcon && breakpointLabel && (
        <Tooltip label={breakpointLabel}>
          <BreakpointIcon
            colorVariant="secondary"
            aria-label={breakpointLabel}
          />
        </Tooltip>
      )}
      {pseudoState && (
        <Badge density="compact" colorVariant="default">
          {`:${pseudoState}`}
        </Badge>
      )}
      <PropertyName
        colorVariant="secondary"
        truncateToLines={1}
        // The row's tooltip already carries the full property name, so `Text`'s
        // own truncation tooltip would only nest a second one inside it.
        truncationTooltipProps={
          asTooltip ? {shouldNotShowTooltip: true} : undefined
        }
      >
        {property}
      </PropertyName>
    </HStack>
  );

  if (!asTooltip) return row;

  return (
    <Tooltip asChild label={`${property} — ${breakpointLabel}`}>
      {row}
    </Tooltip>
  );
};

/**
 * The value half of one drafted change, reading from a persisted summary
 * rather than from the live draft store.
 *
 * Renders what `PropertyValueRenderer` renders for the same change — a
 * variable pill, a colour swatch with the colour in its short form, a swatch
 * for a shadow — minus the `var(--name)` normalization, which needs the
 * variable collections and is only reachable from a live draft.
 */
export const PersistedStyleChangeValue = ({
  property,
  value,
  variableId,
}: {
  property: string;
  value: string;
  variableId?: string;
}) => {
  const variables = useDesignerSelector(selectAllVariables);
  // A summary written without a variable context holds the raw `@var_<id>`
  // token as its value, so an id can also be read out of the value itself.
  const rawTokenId = getWholeValueVariableId(value);
  const resolvedId = (variableId as VariableID | undefined) ?? rawTokenId;

  // A known variable gets its pill, so a rename shows through. So does a token
  // whose variable is gone: `VariablePill` says "Unknown variable", which beats
  // printing `@var_abc123`. Only a summary that captured a display name falls
  // through to text — the name the edit was made against says more than
  // "Unknown variable" does.
  if (resolvedId != null && (variables.get(resolvedId) || rawTokenId != null)) {
    return <VariablePill variableId={resolvedId} variables={variables} />;
  }

  const {displayValue, swatchStyle} = SHADOW_PROPERTIES.has(property)
    ? {
        displayValue: value,
        swatchStyle: resolveShadowSwatchStyle(value, variables),
      }
    : formatPropertyValue(value, property);

  return (
    <HStack
      spacing={1}
      align="center"
      minWidth="$0"
      data-automation-id="style-changes-value"
    >
      {swatchStyle && <ColorSwatch style={swatchStyle} />}
      <Text truncateToLines={1}>{displayValue}</Text>
    </HStack>
  );
};

// The value cell for a text override. Same right-aligned container as the
// style values (`PropertyValueRenderer`) so the two columns line up, with
// `minWidth: 0` so it can shrink within the row.
const TextValueContainer = emotionStyled(HStack)({
  minWidth: 0,
});

// Mirrors `ElementLabel`'s truncation: `minWidth: 0` lets it shrink so
// `text-overflow: ellipsis` kicks in instead of wrapping to a new line (the
// popover is narrow).
const TextValueEllipsis = emotionStyled(Text)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const CONNECTED_PROP_LABEL = 'Connected to a property';

const ConnectedPropIndicatorContainer = emotionStyled(View)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$4',
  height: '$4',
  flexShrink: 0,
});

const ConnectedPropIndicator = () => (
  <Tooltip asChild label={CONNECTED_PROP_LABEL}>
    <Binding2Icon
      colorVariant="secondary"
      aria-label={CONNECTED_PROP_LABEL}
      data-automation-id="style-changes-connected-prop"
    />
  </Tooltip>
);

const ToolbarElementHeader = ({
  layout,
  Icon,
  label,
  hasConnectedProp,
  variant,
  changeCount,
  removeLabel,
  onRemove,
}: {
  layout: ExpandableStyleChangesListLayout;
  Icon: React.ComponentType<Record<string, unknown>>;
  label: string;
  hasConnectedProp?: boolean;
  variant?: StyleChangesElementGroup['variant'];
  changeCount: number;
  removeLabel: string;
  onRemove: () => void;
}) => {
  const {isOpen} = useCollapsible();
  const changeLabel = changeCount === 1 ? 'change' : 'changes';

  return (
    <ElementHeaderRow layout={layout}>
      <ToolbarGroupHeader>
        <CollapsibleTrigger asChild>
          <ToolbarGroupTrigger
            variant="ghost"
            density="compact"
            data-automation-id="style-changes-group-toggle"
          >
            <HStack spacing={2} minWidth="$0">
              <ToolbarGroupIcon>
                <View data-style-changes-element-icon>
                  <Icon />
                </View>
                <ToolbarGroupChevron data-style-changes-chevron>
                  {isOpen ? (
                    <ChevronLargeDownIcon />
                  ) : (
                    <ChevronLargeRightIcon />
                  )}
                </ToolbarGroupChevron>
              </ToolbarGroupIcon>
              <ElementLabel font="heading" truncateToLines={1}>
                {label}
              </ElementLabel>
              {variant && (
                <Badge density="compact" isRounded colorVariant="green">
                  {variant.displayName}
                </Badge>
              )}
            </HStack>
          </ToolbarGroupTrigger>
        </CollapsibleTrigger>
        {hasConnectedProp && (
          <ConnectedPropIndicatorContainer>
            <ConnectedPropIndicator />
          </ConnectedPropIndicatorContainer>
        )}
        <RemoveGroupButtonContainer
          layout={layout}
          data-style-changes-remove-container
        >
          <Tooltip label={removeLabel}>
            <RemoveGroupButton
              Icon={DeleteIcon}
              iconProps={{colorVariant: 'secondary'}}
              label={removeLabel}
              variant="ghost"
              density="compact"
              onClick={onRemove}
              data-automation-id="style-changes-remove-group"
            />
          </Tooltip>
        </RemoveGroupButtonContainer>
        <ToolbarGroupCount colorVariant="secondary">
          {`${changeCount} ${changeLabel}`}
        </ToolbarGroupCount>
      </ToolbarGroupHeader>
    </ElementHeaderRow>
  );
};

interface ListProps {
  groups: StyleChangesElementGroup[];
  emptyText: string;
  layout?: StyleChangesListLayout;
  onRemoveGroup: (nodeId: string, variantId: string | undefined) => void;
}

export const StyleChangesList = ({
  groups,
  emptyText,
  layout = 'default',
  onRemoveGroup,
}: ListProps) => {
  const variables = useDesignerSelector(selectAllVariables);
  const variableCollections = useDesignerSelector(selectVariableCollections);
  const slugMap = useDesignerSelector(selectSlugMap);

  if (groups.length === 0) {
    return (
      <View padding="$3">
        <Text colorVariant="secondary">{emptyText}</Text>
      </View>
    );
  }

  return (
    <ScrollArea spacing={0} layout={layout}>
      {groups.map((group, index) => {
        // The assistant panel starts every group closed, so it stays a
        // glanceable list of elements and their change counts rather than
        // growing a scrollbar once a few properties are queued. The toolbar
        // uses a property-count heuristic instead: it is a transient popover,
        // not a panel the reader sits beside.
        const shouldCollapseGroup =
          layout === 'assistant' ||
          (layout === 'toolbar' && group.changedProperties.length >= 3);
        const {Icon, displayLabel} = getShadowDomElementPresentation(
          group.tagName
        );
        const label =
          layout !== 'default' && group.textContent
            ? group.textContent
            : group.textContent
              ? `${displayLabel} "${group.textContent}"`
              : displayLabel;
        const removeLabel = `Discard changes to ${displayLabel}`;
        const propertyRows = group.changedProperties.map((row, rowIndex) => {
          const rowKey = `${row.property}::${row.breakpointId ?? ''}::${row.pseudoState ?? ''}::${rowIndex}`;
          return (
            <PropertyRow
              key={rowKey}
              layout={layout}
              data-is-last-row={
                rowIndex === group.changedProperties.length - 1 ? '' : undefined
              }
              data-automation-id="style-changes-entry"
              data-property={row.property}
            >
              {/* The breakpoint icon stays a `default`-layout detail: the
                  toolbar and assistant surfaces are narrow, and this is how
                  they have always rendered. Passing `undefined` there keeps
                  that exact behaviour while sharing one definition of a
                  property label with the chat-history card. */}
              <StyleChangePropertyLabel
                property={row.property}
                pseudoState={row.pseudoState}
                breakpointId={
                  layout === 'default' ? row.breakpointId : undefined
                }
              />
              {row.property === 'text' ? (
                <TextValueContainer
                  spacing={1}
                  align="center"
                  data-automation-id="style-changes-value"
                >
                  <TextValueEllipsis>{row.value}</TextValueEllipsis>
                </TextValueContainer>
              ) : (
                <PropertyValueRenderer
                  property={row.property}
                  value={row.value}
                  variables={variables}
                  variableCollections={variableCollections}
                  slugMap={slugMap}
                />
              )}
            </PropertyRow>
          );
        });

        if (layout !== 'default') {
          return (
            <Collapsible
              key={`${group.key}:${shouldCollapseGroup}`}
              defaultIsOpen={!shouldCollapseGroup}
            >
              <VStack spacing={0} width="100%">
                <ToolbarElementHeader
                  layout={layout}
                  Icon={Icon}
                  label={label}
                  hasConnectedProp={group.hasConnectedProp}
                  variant={group.variant}
                  changeCount={group.changedProperties.length}
                  removeLabel={removeLabel}
                  onRemove={() =>
                    onRemoveGroup(group.nodeId, group.variant?.id)
                  }
                />
                <CollapsibleContent>{propertyRows}</CollapsibleContent>
              </VStack>
            </Collapsible>
          );
        }

        return (
          <React.Fragment key={group.key}>
            {index > 0 && <GroupDivider />}
            <ElementHeaderRow layout="default">
              <Icon />
              <ElementLabel font="heading" truncationLimit={24}>
                {label}
              </ElementLabel>
              {group.hasConnectedProp && <ConnectedPropIndicator />}
              {group.variant && (
                <Badge density="compact" isRounded colorVariant="green">
                  {group.variant.displayName}
                </Badge>
              )}
              <RemoveGroupButtonContainer layout="default">
                <Tooltip label={removeLabel}>
                  <RemoveGroupButton
                    Icon={DeleteIcon}
                    iconProps={{colorVariant: 'secondary'}}
                    label={removeLabel}
                    variant="ghost"
                    density="compact"
                    onClick={() =>
                      onRemoveGroup(group.nodeId, group.variant?.id)
                    }
                    data-automation-id="style-changes-remove-group"
                  />
                </Tooltip>
              </RemoveGroupButtonContainer>
            </ElementHeaderRow>
            {propertyRows}
          </React.Fragment>
        );
      })}
    </ScrollArea>
  );
};
